import { stableFingerprint } from './random.js';
import { DUNGEON_ENTRANCE_GENERATOR_VERSION, DUNGEON_ENTRANCE_SCHEMA_VERSION, DungeonEntranceError, } from './entrance-types.js';
import { validateDungeonEntranceScenePlan, validateDungeonEntranceSite, validateDungeonEntranceState, } from './entrance-validate.js';
function portalStateFor(state) {
    if (state.phase === 'primed')
        return 'ready';
    if (state.phase === 'faulted')
        return 'sealed';
    return 'quiet';
}
/** Produces local semantic geometry plus dynamic omen slots; a renderer supplies real meshes. */
export function planDungeonEntranceScene(site, profile, state) {
    const inputProblems = [
        ...validateDungeonEntranceSite(site, profile),
        ...validateDungeonEntranceState(site, state),
    ];
    if (inputProblems.length > 0)
        throw new DungeonEntranceError(inputProblems);
    const parts = [];
    const add = (part) => {
        parts.push(part);
    };
    const baseRise = 0.34;
    const platformTop = profile.platformTiers * baseRise;
    for (let tier = 0; tier < profile.platformTiers; tier += 1) {
        const inset = tier * 0.42 * profile.scale;
        const diameter = Math.max(profile.portalWidth + 2, profile.footprintRadius * 2 - inset * 2);
        add({
            id: `platform-${tier + 1}`,
            kind: 'platform',
            primitive: 'cylinder',
            position: { x: 0, y: baseRise * (tier + 0.5), z: 0 },
            scale: { x: diameter, y: baseRise, z: diameter },
            rotationY: 0,
            materialKey: 'stone',
        });
    }
    for (let layer = 0; layer < profile.archLayers; layer += 1) {
        const expansion = layer * 0.38 * profile.scale;
        add({
            id: `arch-${layer + 1}`,
            kind: 'arch',
            primitive: 'arch',
            position: {
                x: 0,
                y: platformTop + profile.portalHeight * 0.5 + expansion * 0.15,
                z: 0.18 * layer,
            },
            scale: {
                x: profile.portalWidth + expansion,
                y: profile.portalHeight + expansion * 0.45,
                z: 0.38 + layer * 0.1,
            },
            rotationY: 0,
            materialKey: layer === profile.archLayers - 1 ? 'trim' : 'stone',
        });
    }
    for (let pair = 0; pair < profile.columnPairs; pair += 1) {
        const depthBand = pair % Math.max(1, profile.archLayers);
        const outwardBand = Math.floor(pair / Math.max(1, profile.archLayers));
        const x = profile.portalWidth * 0.5 + 0.55 + outwardBand * 0.42;
        const z = 0.15 + depthBand * 0.35;
        for (const side of [-1, 1]) {
            add({
                id: `column-${pair + 1}-${side < 0 ? 'left' : 'right'}`,
                kind: 'column',
                primitive: 'cylinder',
                position: { x: side * x, y: platformTop + profile.portalHeight * 0.45, z },
                scale: {
                    x: 0.42 * profile.scale,
                    y: profile.portalHeight * 0.9,
                    z: 0.42 * profile.scale,
                },
                rotationY: 0,
                materialKey: pair % 2 === 0 ? 'stone' : 'trim',
            });
        }
    }
    for (let pair = 0; pair < profile.buttressPairs; pair += 1) {
        const x = profile.portalWidth * 0.5 + 1.05 + pair * 0.48;
        for (const side of [-1, 1]) {
            add({
                id: `buttress-${pair + 1}-${side < 0 ? 'left' : 'right'}`,
                kind: 'buttress',
                primitive: 'box',
                position: { x: side * x, y: platformTop + profile.portalHeight * 0.28, z: 0.5 },
                scale: {
                    x: 0.58 * profile.scale,
                    y: profile.portalHeight * 0.56,
                    z: 1.05 * profile.scale,
                },
                rotationY: side * -0.13,
                materialKey: 'stone',
            });
        }
    }
    // O-025 item 1: spires must SEAT on the structure beneath them. The original plan centred
    // each spire at `platformTop + portalHeight + 0.55`, so its underside sat above the columns
    // (which top out at 0.9*portalHeight) — the same floating-spire defect the owner reported in
    // the review meshes, and visible in the techdemo shrine. The tip is held at its original
    // height and the spire is grown downward onto its support, so the silhouette is unchanged.
    const spireColumnTop = platformTop + profile.portalHeight * 0.9;
    const spireButtressTop = platformTop + profile.portalHeight * 0.56;
    const spireSupports = [];
    for (let pair = 0; pair < profile.columnPairs; pair += 1) {
        const outwardBand = Math.floor(pair / Math.max(1, profile.archLayers));
        const centre = profile.portalWidth * 0.5 + 0.55 + outwardBand * 0.42;
        const half = 0.21 * profile.scale;
        spireSupports.push({ min: centre - half, max: centre + half, top: spireColumnTop });
    }
    for (let pair = 0; pair < profile.buttressPairs; pair += 1) {
        const centre = profile.portalWidth * 0.5 + 1.05 + pair * 0.48;
        const half = 0.58 * profile.scale * 0.5;
        spireSupports.push({ min: centre - half, max: centre + half, top: spireButtressTop });
    }
    for (let index = 0; index < profile.spireCount; index += 1) {
        const side = index % 2 === 0 ? -1 : 1;
        const band = Math.floor(index / 2);
        const centre = profile.portalWidth * 0.5 + 0.85 + band * 0.62;
        const x = side * centre;
        const half = 0.14 * profile.scale;
        const tipTop = platformTop + profile.portalHeight + 0.55 + band * 0.16 + 0.55 * profile.scale;
        const carried = spireSupports.filter((support) => support.max >= centre - half && support.min <= centre + half);
        // Fall back to the platform if a future profile puts a spire over nothing.
        const seatY = carried.length ? Math.max(...carried.map((support) => support.top)) : platformTop;
        const spireHeight = Math.max(0.1, tipTop - seatY);
        add({
            id: `spire-${index + 1}`,
            kind: 'spire',
            primitive: 'cylinder',
            position: { x, y: seatY + spireHeight * 0.5, z: 0.25 },
            scale: { x: 0.28 * profile.scale, y: spireHeight, z: 0.28 * profile.scale },
            rotationY: 0,
            materialKey: 'trim',
        });
    }
    if (profile.crown !== 'none') {
        add({
            id: `crown-${profile.crown}`,
            kind: 'crown',
            primitive: profile.crown === 'broken-crown' ? 'ring' : 'box',
            position: { x: 0, y: platformTop + profile.portalHeight + 0.72, z: 0.12 },
            scale: {
                x: profile.portalWidth * (profile.crown === 'broken-crown' ? 0.72 : 0.45),
                y: 0.42 * profile.scale,
                z: 0.3 * profile.scale,
            },
            rotationY: profile.crown === 'split' ? 0.18 : 0,
            materialKey: 'trim',
        });
    }
    const portalState = portalStateFor(state);
    add({
        id: 'portal-door',
        kind: 'portal',
        primitive: 'plane',
        position: { x: 0, y: platformTop + profile.portalHeight * 0.5, z: -0.05 },
        scale: { x: profile.portalWidth * 0.82, y: profile.portalHeight * 0.88, z: 0.05 },
        rotationY: 0,
        materialKey: portalState === 'ready'
            ? 'portalReady'
            : portalState === 'sealed'
                ? 'portalSealed'
                : 'portalQuiet',
        accessibleLabel: portalState === 'ready'
            ? 'Dungeon portal ready for entry'
            : portalState === 'sealed'
                ? 'Dungeon portal sealed due to a generation fault'
                : 'Dungeon portal dormant',
    });
    const panelY = platformTop + profile.portalHeight + 0.18;
    add({
        id: 'enemy-omen-panel',
        kind: 'omen-panel',
        primitive: 'box',
        position: { x: 0, y: panelY, z: 0.48 },
        scale: { x: Math.max(2.7, profile.portalWidth * 0.82), y: 0.72, z: 0.2 },
        rotationY: 0,
        materialKey: 'panel',
        visibleWithin: site.radii.previewRevealRadius,
        accessibleLabel: state.phase === 'primed' ? state.preview.accessibleLabel : 'Enemy omens shrouded',
    });
    // O-025 item 1: the omen panel sits forward of the arch (z0.38..0.58) so its markers read
    // head-on, which left it visually unsupported. Two corbels carry it back onto the arch face
    // — the markers keep their exact positions, the panel stops looking like it hovers.
    for (const side of [-1, 1]) {
        add({
            id: `omen-corbel-${side < 0 ? 'left' : 'right'}`,
            kind: 'omen-corbel',
            primitive: 'box',
            position: { x: side * profile.portalWidth * 0.26, y: panelY - 0.2, z: 0.24 },
            scale: { x: 0.22 * profile.scale, y: 0.28, z: 0.52 },
            rotationY: 0,
            materialKey: 'stone',
            visibleWithin: site.radii.previewRevealRadius,
        });
    }
    // Owner-rule specific-enemy markers (O-025) drive the physical slots when present;
    // the broad-family preview remains the fallback for pre-marker primed states.
    const markerSlots = state.phase === 'primed' && state.markers && state.markers.length > 0
        ? state.markers.map((marker) => ({
            iconId: marker.iconId,
            symbolKey: marker.symbolKey,
            bossMarked: marker.isBoss,
            accessibleLabel: `${marker.label}${marker.isBoss ? ' — boss' : ` ×${marker.plannedCount}`}. ${marker.preparationHint}`,
        }))
        : state.phase === 'primed'
            ? state.preview.slots.map((preview) => ({
                iconId: preview.iconId,
                symbolKey: preview.symbolKey,
                bossMarked: preview.containsBoss,
                accessibleLabel: `${preview.label}${preview.containsBoss ? ', includes a boss' : ''}. ${preview.preparationHint}`,
            }))
            : [];
    for (let slot = 0; slot < 3; slot += 1) {
        const filled = markerSlots[slot];
        add({
            id: `enemy-icon-slot-${slot + 1}`,
            kind: 'icon-slot',
            primitive: 'icon',
            position: { x: (slot - 1) * 0.82, y: panelY, z: 0.61 },
            scale: { x: 0.55, y: 0.55, z: 0.05 },
            rotationY: 0,
            materialKey: 'trim',
            visibleWithin: site.radii.previewRevealRadius,
            ...(filled
                ? {
                    iconId: filled.iconId,
                    symbolKey: filled.symbolKey,
                    bossMarked: filled.bossMarked,
                    accessibleLabel: filled.accessibleLabel,
                }
                : { accessibleLabel: 'Shrouded enemy omen' }),
        });
    }
    add({
        id: 'portal-interact-anchor',
        kind: 'interact-anchor',
        primitive: 'ring',
        position: { x: 0, y: platformTop + 0.03, z: 0.55 },
        scale: { x: profile.portalWidth * 0.46, y: 0.06, z: profile.portalWidth * 0.46 },
        rotationY: 0,
        materialKey: 'interact',
        visibleWithin: site.radii.interactionRadius,
        accessibleLabel: 'Enter dungeon',
    });
    const accessibleLabel = `${profile.name} dungeon shrine. ${state.phase === 'primed'
        ? state.preview.accessibleLabel
        : state.phase === 'faulted'
            ? 'Portal sealed.'
            : 'Enemy omens not yet revealed.'}`;
    const material = {
        schemaVersion: DUNGEON_ENTRANCE_SCHEMA_VERSION,
        generatorVersion: DUNGEON_ENTRANCE_GENERATOR_VERSION,
        siteId: site.id,
        difficultyId: site.difficultyId,
        shrineProfileId: profile.id,
        worldPosition: { ...site.position },
        rotationQuarterTurns: site.rotationQuarterTurns,
        portalState,
        portalIntensity: portalState === 'ready' ? profile.portalIntensity : profile.portalIntensity * 0.35,
        footprintRadius: profile.footprintRadius,
        interactionRadius: site.radii.interactionRadius,
        previewRevealRadius: site.radii.previewRevealRadius,
        ...(state.phase === 'primed' ? { previewFingerprint: state.preview.planFingerprint } : {}),
        accessibleLabel,
        materials: { ...profile.materials },
        parts,
    };
    const plan = {
        ...material,
        fingerprint: stableFingerprint(material),
    };
    const problems = validateDungeonEntranceScenePlan(plan);
    if (problems.length > 0)
        throw new DungeonEntranceError(problems);
    return plan;
}
//# sourceMappingURL=entrance-scene.js.map